<template>
  <div>
    <PrimeInputNumber
      :model-value="modelValue ?? 0"
      @update:model-value="onModelUpdate"
      unstyled
      :pt="passThrough"
      :disabled="state === InputState.DISABLED"
      :min="min"
      :max="max"
      :step="step"
      :show-buttons="showButtons"
      :button-layout="buttonLayout"
      :min-fraction-digits="minFractionDigits"
      :max-fraction-digits="maxFractionDigits"
      :use-grouping="useGrouping"
      :mode="mode"
      :currency="currency"
      :locale="locale"
      v-bind="$attrs"
    >
      <template #incrementicon>
        <MeridiaIcon name="add" type="material-icons-outlined" class="meridia-inputnumber-icon text-primary" />
      </template>
      <template #decrementicon>
        <MeridiaIcon name="remove" type="material-icons-outlined" class="meridia-inputnumber-icon text-primary" />
      </template>
    </PrimeInputNumber>
    <transition name="fade">
      <span
        v-if="helper_text"
        class="helper-text"
        :class="
          getPt<PassThroughMeridiaInputNumber>('helper_text', passThrough)?.class
        "
        >{{ helper_text }}</span
      >
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  InputSize,
  InputState,
  PassThroughMeridiaInputNumber,
} from './types';
import { usePassThrough } from 'primevue/passthrough';
import PrimeInputNumber from 'primevue/inputnumber';
import MeridiaIcon from '../../icon/MeridiaIcon.vue';
import { getPt } from '../../utils';

const modelValue = defineModel<number | null>({ required: true });

function onModelUpdate(value: number | null) {
  modelValue.value = value !== null ? value : 0;
}

const props = withDefaults(
  defineProps<{
    size?: `${InputSize}`;
    helper_text?: string;
    state?: `${InputState}`;
    min?: number;
    max?: number;
    step?: number;
    showButtons?: boolean;
    buttonLayout?: 'horizontal' | 'vertical' | 'stacked';
    minFractionDigits?: number;
    maxFractionDigits?: number;
    useGrouping?: boolean;
    mode?: 'decimal' | 'currency';
    currency?: string;
    locale?: string;
    pt?: PassThroughMeridiaInputNumber;
  }>(),
  {
    size: InputSize.SMALL,
    showButtons: true,
    buttonLayout: 'horizontal',
    useGrouping: false,
    mode: 'decimal',
  }
);

const sizeClass = computed(() => ({
  'size-small': props.size === InputSize.SMALL,
  'size-large': props.size === InputSize.LARGE,
}));

const stateClass = computed(() => ({
  'state-error': props.state === InputState.ERROR,
  'state-success': props.state === InputState.SUCCESS,
  'state-disabled': props.state === InputState.DISABLED,
}));

const rootClass = computed(() => {
  const size = Object.entries(sizeClass.value)
    .filter(([, v]) => v)
    .map(([k]) => k)
    .join(' ');
  const state = Object.entries(stateClass.value)
    .filter(([, v]) => v)
    .map(([k]) => k)
    .join(' ');
  const layout =
    props.buttonLayout === 'vertical' ? 'layout-vertical' : '';
  return ['meridia-inputnumber-wrapper', layout, size, state]
    .filter(Boolean)
    .join(' ');
});

const passThrough = usePassThrough(
  props.pt,
  {
    root: () => ({
      class: rootClass.value,
    }),
    pcInputText: {
      root: () => ({
        class: 'meridia-inputnumber-input',
      }),
    },
    incrementButton: () => ({
        class: 'meridia-inputnumber-increment-button',
    }),
    decrementButton: () => ({
      class: 'meridia-inputnumber-decrement-button',
    }),
  } as PassThroughMeridiaInputNumber,
  { mergeSections: false, mergeProps: true }
) as PassThroughMeridiaInputNumber;
</script>

<style lang="scss" scoped>
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease-out;
}
</style>

<style src="./style.scss" scoped lang="scss"></style>
